'use client';

import OptimizedImage from '@/components/atoms/OptimizedImage';
import type { ResponsiveImageData } from '@/components/atoms/ResponsiveImage';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import type { PagePart_Image_Entry } from '@/types/graphql';
import Link from 'next/link';

export default function PagePartImage({ pagePart }: { pagePart: PagePart_Image_Entry }) {
    const images = (pagePart.image?.filter(Boolean) ?? []) as ResponsiveImageData[];

    if (images.length === 0) {
        return null;
    }

    return (
        <section className={'py-12'} aria-label="Image gallery">
            <Swiper
                spaceBetween={50}
                slidesPerView={1}
                navigation={true}
                keyboard={{
                    enabled: true,
                    onlyInViewport: true,
                    pageUpDown: true,
                }}
                modules={[Navigation, Keyboard]}
                className={'pb-12 text-sm leading-6'}
                tabIndex={0}
                aria-roledescription="carousel"
            >
                {images.map((image, index) => (
                    <SwiperSlide
                        key={image?.id}
                        role="tabpanel"
                        aria-roledescription="slide"
                        aria-label={`Slide ${index + 1} of ${images.length}`}
                    >
                        {pagePart.buttonLink ? (
                            <Link
                                passHref
                                href={pagePart.buttonLink}
                                target={pagePart.openInNewTab ? '_blank' : '_self'}
                                aria-label={`View image: ${image?.alt ?? 'Untitled image'}`}
                            >
                                <OptimizedImage
                                    image={image}
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                                    priority={index === 0}
                                />
                            </Link>
                        ) : (
                            <OptimizedImage
                                image={image}
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                                priority={index === 0}
                            />
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
        </section>
    );
}
